import { useState } from 'react';
import { ReflectionChat } from '../components/reflection/ReflectionChat';
import { SummaryDisplay } from '../components/reflection/SummaryDisplay';
import { updateLearnerState } from '@/lib/api';
import type { AgentConfig } from '@/lib/types';

interface ReflectionPageProps {
  learnerId: string;
  moduleId: string;
  partId: string;
  agentConfig: AgentConfig;
  onComplete: () => void;
}

export function ReflectionPage({
  learnerId,
  moduleId,
  partId,
  agentConfig,
  onComplete,
}: ReflectionPageProps) {
  const [summaries, setSummaries] = useState<string[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleSummaryExtracted = (summary: string) => {
    setSummaries(prev => {
      if (prev.includes(summary)) return prev;
      return [...prev, summary];
    });
  };

  const handleComplete = async () => {
    setIsCompleting(true);

    try {
      const fullSummary = summaries.join(' ');
      await updateLearnerState({
        learnerId,
        moduleId,
        reflectionUpdate: {
          partId,
          summary: fullSummary,
          completed: true,
        },
      });

      onComplete();
    } catch (error) {
      console.error('Failed to save reflection:', error);
      setIsCompleting(false);
    }
  };

  const handleSkip = async () => {
    setIsCompleting(true);

    try {
      // Save whatever summary exists (may be empty)
      const fullSummary = summaries.join(' ') || 'Reflection skipped';
      await updateLearnerState({
        learnerId,
        moduleId,
        reflectionUpdate: {
          partId,
          summary: fullSummary,
          completed: false,
        },
      });

      onComplete();
    } catch (error) {
      console.error('Failed to save skip state:', error);
      setIsCompleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      {/* Header — compact and elegant */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium mb-4">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Reflection
        </div>
        <h1 className="text-2xl font-serif font-bold text-slate-900 dark:text-slate-100">
          Connect the Concepts
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">
          Think through four scenarios, then craft your position
        </p>
      </div>

      {/* Summaries */}
      {summaries.length > 0 && (
        <div className="mb-6">
          <SummaryDisplay summaries={summaries} />
        </div>
      )}

      {/* Chat area — clean and spacious */}
      <div className="bg-white dark:bg-slate-800/80 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="min-h-[460px] flex flex-col">
          <ReflectionChat
            learnerId={learnerId}
            moduleId={moduleId}
            partId={partId}
            agentConfig={agentConfig}
            onSummaryExtracted={handleSummaryExtracted}
            onComplete={handleComplete}
            onSkip={handleSkip}
          />
        </div>
      </div>

      {isCompleting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-slate-600 dark:text-slate-400">Saving your reflection...</p>
          </div>
        </div>
      )}
    </div>
  );
}
